/**
 * Uplifted from `data/ga-constants.ts` (web) and `constants/analytics.ts` (mobile) — the COMMON
 * half, seeded with only what the uplifted sagas actually reference.
 *
 * ⚠️ **The two sources declare these as different TS constructs: web as `const` objects, mobile
 * as `enum`s.** The values are identical, so the DATA is shared here; each variant keeps its own
 * platform-only extras. `as const` is used rather than `enum` so a shared file emits no runtime
 * enum object into the other platform's bundle, and so web's existing `GA_CATEGORIES.SALE`
 * property access keeps working unchanged.
 *
 * 🔴 **This does NOT make `GA_EVENTS` usable as a TYPE on mobile.** Mobile's original is an
 * `enum`, which is both a value and a type; a `const` object is only a value. Nothing in the
 * uplifted sagas uses it as a type — checked before authoring — but a future mobile consumer
 * that writes `x: GA_EVENTS` needs `GaEvent` below instead.
 *
 * The folder mirrors the SOURCE FILE. Web's is `data/ga-constants`, mobile's lives under a
 * `constants/` directory web does not have; `data/gaConstants` is the web-side mirror because
 * that is the file both platforms' sagas were reading through.
 */

/** The events BOTH platforms declare, with identical values. Diffed, not assumed. */
export const GA_EVENTS = {
  RECOMMENDER_WIDGET_CLOSED: "recommender_widget_closed",
  RECOMMENDER_WIDGET_OPENED: "recommender_widget_opened",
  RECOMMENDATIONS_SHOWN: "recommendations_shown",
  ITEM_ADDED_TO_CART: "item_added_to_cart",
  ITEM_REMOVED_FROM_CART: "item_removed_from_cart",
  ITEM_QTY_CHANGED: "item_qty_changed",
  SALE_STARTED: "sale_started",
  RECOMMENDATIONS_FETCHED: "recommendations_fetched",
  ORDER_COMPLETED: "order_completed",
  ITEM_SEARCH_VIEW_OPENED: "item_search_view_opened",
} as const;

/** The type mobile's `enum` provided for free. Use this where a value's TYPE is needed. */
export type GaEvent = (typeof GA_EVENTS)[keyof typeof GA_EVENTS];

/**
 * ⚠️ Only `SALE` is shared. Web additionally declares `TESTING`, which mobile does not — see
 * `index.desktop.ts`. Collapsing them would hand the other platform a category it never had.
 */
export const GA_CATEGORIES = {
  SALE: "Sale",
} as const;

/** Byte-identical in both apps. */
export const ITEM_SOURCES = {
  RECOMMENDATIONS: "Recommendations",
  CATALOG: "Catalog",
  SEARCH: "Search",
} as const;

/** Empty in both apps. Kept so the export surface matches, exactly as the app files have it. */
export const GA_LABELS = {};
